import { Controller } from "@hotwired/stimulus"
import type { TurboSubmitStartEvent } from "@hotwired/turbo"
import * as Turbo from "@hotwired/turbo"
import AudioRecorder from "../../audioRecorder"
import { DateTime as DT } from "luxon"
import type FlatpickrController from "../flatpickr_controller"

export default class ComposeMessageController extends Controller {

  declare frameTarget: HTMLElement
  declare modalTarget: HTMLDivElement
  declare previewTarget: HTMLDivElement
  declare scheduledAtFieldTarget: HTMLInputElement
  declare smsMessageTarget: HTMLTextAreaElement
  declare templateNameFieldTarget: HTMLInputElement
  declare timezoneFieldTarget: HTMLInputElement
  declare flatpickrOutlets: FlatpickrController[]

  originalSrc: string | undefined = undefined

  static outlets = ["flatpickr"]

  static targets = [
    "frame",
    "modal",
    "preview",
    "scheduledAtField",
    "smsMessage",
    "templateNameField",
    "timezoneField",
  ]

  connect() {
    this.modalTarget.addEventListener("show.bs.modal", (e) => {
      const event = e as MouseEvent

      if (event.relatedTarget instanceof HTMLElement) {
        const originalSrc = event.relatedTarget.dataset.modalSrc
        if (originalSrc) {
          this.frameTarget.innerHTML = ""
          this.originalSrc = originalSrc
          this.frameTarget.setAttribute("src", originalSrc)
        }
      }
    })

    this.modalTarget.addEventListener("hidden.bs.modal", () => {
      this.refreshDraftsList()
    })
  }

  // Editing a draft autosaves through the wizard; refresh the Drafts index on close.
  refreshDraftsList() {
    if (document.getElementById("drafts-results")) {
      Turbo.visit(window.location.href, { frame: "drafts-results" })
    }
  }

  previewTargetConnected(el: HTMLDivElement) {
    const audio = el.querySelector("audio")
    if (audio?.src) {
      const audioRecorder = new AudioRecorder(audio)
      audioRecorder.fixMetadata()
    }
  }

  insertSmsToken() {
    const selected = this.smsMessageTarget.selectionEnd
    this.smsMessageTarget.setRangeText("{{firstname}}", selected, selected, "end")
    this.smsMessageTarget.focus()
  }

  toggleTemplateNameDisabled() {
    this.templateNameFieldTarget.disabled = !this.templateNameFieldTarget.disabled
  }

  buildDateTime() {
    const datePicker = this.flatpickrOutlets.find(o => o.modeValue === "date")
    const timePicker = this.flatpickrOutlets.find(o => o.modeValue === "time")

    if (!datePicker || !timePicker) {
      throw new Error("Date and time pickers must be connected")
    }

    const selectedDate = datePicker.selectedDates[0]
    const selectedTime = timePicker.selectedDates[0]

    if (!selectedDate || !selectedTime) {
      throw new Error("Date and time must be selected")
    }

    const date = DT.fromJSDate(selectedDate)
    const time = DT.fromJSDate(selectedTime)

    const value = DT.fromObject(
      {
        year: date.year,
        month: date.month,
        day: date.day,
        hour: time.hour,
        minute: time.minute,
      },
      {
        zone: this.timezoneFieldTarget.value,
      }
    ).toISO()

    return value || ""
  }

  submitSchedule(event: TurboSubmitStartEvent) {
    const dateTime = this.buildDateTime()
    event.detail.formSubmission.fetchRequest.body.set("message_draft[scheduled_at]", dateTime)
  }
}
